import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { MatchRecordForm } from "@/components/MatchRecordForm";
import { MatchSummaryDialog } from "@/components/MatchSummaryDialog";
import { ArrowLeft, Trophy } from "lucide-react";
import { useState, useMemo } from "react";
import { useSearch } from "wouter";

interface League {
  id: string;
  name: string;
  homeClubId?: string | null;
  isFavorite?: boolean | null;
}

interface LeagueMember {
  id: string;
  name: string;
  rating: number;
  role?: string;
}

export function RecordMatchPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [matchSummary, setMatchSummary] = useState<any>(null);
  // Bumps every time the form should be considered "freshly
  // opened" by the user. We use it as a `key` on MatchRecordForm
  // so that any flow which would re-show the form on the same
  // page mount — for example returning to the form view after the
  // summary dialog without navigating away — still gets a fresh
  // form instance with the "Partido Competitivo" toggle reset to
  // checked, instead of relying solely on route-level unmounting.
  const [formSession, setFormSession] = useState(0);
  
  const searchString = useSearch();
  const fixedLeagueId = useMemo(() => {
    const params = new URLSearchParams(searchString);
    return params.get("leagueId") || undefined;
  }, [searchString]);

  const { data: leagues = [] } = useQuery<League[]>({
    queryKey: ["/api/leagues"],
  });

  type ProfileLike = {
    country?: string | null;
    homeClubId?: string | null;
  } | null | undefined;
  const userProfile = user?.profile as ProfileLike;
  
  const { data: fixedLeagueData } = useQuery<{ league: any; members: LeagueMember[] }>({
    queryKey: ["/api/leagues", fixedLeagueId, "detail"],
    queryFn: async () => {
      const response = await fetch(`/api/leagues/${fixedLeagueId}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch league');
      return response.json();
    },
    enabled: !!fixedLeagueId,
  });
  
  const isAdminOfFixedLeague = useMemo(() => {
    if (!fixedLeagueData?.members || !user?.id) return false;
    const currentMember = fixedLeagueData.members.find((m: any) => m.userId === user.id);
    return currentMember?.role === "owner" || currentMember?.role === "admin";
  }, [fixedLeagueData, user?.id]);

  const createMatchMutation = useMutation({
    mutationFn: async (data: {
      team1Player1?: string;
      partner: string;
      opponent1: string;
      opponent2: string;
      score: { myTeam: number; opponents: number }[];
      competitive: boolean;
      leagueIds: string[];
      homeClubId?: string | null;
    }) => {
      if (data.score.some(s => s.myTeam === s.opponents)) {
        throw new Error("Cada set debe tener un ganador (sin empates).");
      }
      if (data.leagueIds.length === 0) {
        const winner = data.score.filter(s => s.myTeam > s.opponents).length > 
                       data.score.filter(s => s.opponents > s.myTeam).length ? 1 : 2;
        const scoreObj = { 
          sets: data.score.map(s => ({ team1: s.myTeam, team2: s.opponents })) 
        };
        
        return apiRequest('POST', '/api/matches/global', {
          team1Player2Id: data.partner || null,
          team2Player1Id: data.opponent1,
          team2Player2Id: data.opponent2 || null,
          winner,
          score: scoreObj,
          notes: data.competitive ? "Partido competitivo" : "Partido amistoso",
          competitive: data.competitive,
          homeClubId: data.homeClubId ?? null,
        });
      } else {
        // Record match in each selected league
        const scoreObj = { sets: data.score.map(s => ({ team1: s.myTeam, team2: s.opponents })) };
        const winner = data.score.filter(s => s.myTeam > s.opponents).length > 
                      data.score.filter(s => s.opponents > s.myTeam).length ? 1 : 2;
        
        // team1Player1 is always set (either by user selection or currentUserId)
        const team1Player1Id = data.team1Player1;
        
        const matchPayload = {
          team1Player1Id,
          team1Player2Id: data.partner || null,
          team2Player1Id: data.opponent1,
          team2Player2Id: data.opponent2 || null,
          winner,
          score: scoreObj,
          notes: data.competitive ? "Partido competitivo" : "Partido amistoso",
          competitive: data.competitive,
          homeClubId: data.homeClubId ?? null,
        };
        
        const firstRequest = apiRequest('POST', `/api/leagues/${data.leagueIds[0]}/matches`, matchPayload);
        
        // Record in additional leagues if any
        if (data.leagueIds.length > 1) {
          await Promise.all(data.leagueIds.slice(1).map(leagueId =>
            apiRequest('POST', `/api/leagues/${leagueId}/matches`, matchPayload)
          ));
        }
        
        return firstRequest;
      }
    },
    onSuccess: async (response, variables) => {
      if (response.ok) {
        const result = await response.json();
        queryClient.invalidateQueries({ queryKey: ["/api/matches/pending"] });
        queryClient.invalidateQueries({ queryKey: ["/api/matches/global/pending"] });
        queryClient.invalidateQueries({ queryKey: ["/api/profile/match-history"] });
        queryClient.invalidateQueries({ queryKey: ["/api/leagues"] });
        if (variables.leagueIds) {
          variables.leagueIds.forEach((lid: string) => {
            queryClient.invalidateQueries({ queryKey: ["/api/leagues", lid] });
          });
        }

        if (result.summary) {
          // Add flag to indicate if current user is a participant
          result.summary.isUserParticipant = !isAdminOfFixedLeague || (isAdminOfFixedLeague && user?.id === result.summary.myTeam.players[0]);
          setMatchSummary(result.summary);
        } else {
          toast({
            title: "¡Partido Registrado!",
            description: "El partido ha sido guardado. Pendiente de confirmación del rival.",
          });
          history.back();
        }
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.message || "No se pudo registrar el partido",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Error de conexión al registrar el partido",
        variant: "destructive",
      });
    },
  });

  const handleLeagueChange = async (leagueId: string): Promise<LeagueMember[]> => {
    const response = await fetch(`/api/leagues/${leagueId}`, { 
      credentials: 'include',
      method: 'GET',
    });
    if (!response.ok) throw new Error('Failed to fetch league members');
    const data = await response.json();
    // Map API response to expected format (API uses userId, we need id)
    return (data.members || []).map((member: any) => ({
      id: member.userId,
      name: member.userName,
      rating: member.rating || 1000,
    }));
  };

  return (
    <div 
      className="flex flex-col bg-background overflow-hidden"
      style={{ height: '100dvh' }}
    >
      <header className="flex-shrink-0 bg-background/95 backdrop-blur border-b z-50 safe-area-top">
        <div className="container max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => history.back()}
            data-testid="button-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">Registrar Partido</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto overscroll-contain">
        <div className="container max-w-2xl mx-auto px-4 pt-3 pb-4 safe-area-bottom">
        {/*
          Optimistic close: once the user taps "Registrar partido"
          we hide the form behind a lightweight "Procesando partido…"
          state so it visibly disappears immediately. The
          MatchSummaryDialog below renders independently and
          surfaces the auto-approval / pending result popup as soon
          as the API responds. On dialog dismissal we navigate back.
        */}
        {createMatchMutation.isPending || matchSummary ? (
          <div
            className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground"
            data-testid="status-record-processing"
          >
            <Trophy className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm font-medium">Procesando partido…</p>
            <p className="text-xs mt-1">Estamos guardando el resultado.</p>
          </div>
        ) : (
          <MatchRecordForm
            key={`record-match-form-${formSession}`}
            leagues={fixedLeagueId && fixedLeagueData ? [{ id: fixedLeagueId, name: fixedLeagueData.league?.name || "Liga" }] : leagues}
            currentUserId={user?.id}
            currentUserName={user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email || "Jugador"}
            onLeagueChange={handleLeagueChange}
            onSubmit={(data) => {
              createMatchMutation.mutate(data);
            }}
            fixedLeagueId={fixedLeagueId}
            isAdminMode={isAdminOfFixedLeague}
            fixedLeagueMembers={fixedLeagueData?.members?.map((m: any) => ({
              id: m.id,  // Already userId from server
              name: m.userName,
              rating: m.rating || 1000
            })) || []}
            matchFormat={fixedLeagueData?.league?.matchFormat || "doubles"}
            leagueDefaultHomeClubId={fixedLeagueData?.league?.homeClubId || null}
            profileHomeClubId={userProfile?.homeClubId || null}
            country={userProfile?.country || null}
          />
        )}
        </div>
      </main>

      {/* Match Summary Dialog */}
      <MatchSummaryDialog
        open={!!matchSummary}
        onClose={() => {
          setMatchSummary(null);
          // Bump the form session so that if the user lands back
          // on this page (or a future flow re-shows the form
          // without a route change) the form is mounted fresh
          // with the "Partido Competitivo" toggle reset.
          setFormSession((s) => s + 1);
          history.back();
        }}
        summary={matchSummary}
        isAdmin={isAdminOfFixedLeague}
        onGoToLeaguePanel={
          fixedLeagueId
            ? () => {
                history.back();
                // Let the current page unload before navigating so the
                // league admin panel opens on a clean history entry.
                setTimeout(() => {
                  window.location.href = `/liga/${fixedLeagueId}?tab=matches`;
                }, 80);
              }
            : undefined
        }
      />
    </div>
  );
}
